import * as Blockly from 'blockly/core'
import { luaGenerator } from 'blockly/lua'

import { controlBlocks, controlDefaults } from './control'
import { emptyNumberBlocks, emptyNumberDefaults } from './emptyNumber'
import { eventBlocks, eventDefaults } from './events'
import { looksBlocks, looksDefaults } from './looks'
import { registerMessageCallbacks } from './messages'
import { registerMissionCallbacks } from './mission'
import { movementBlocks, movementDefaults } from './movement'
import { operatorBlocks, operatorDefaults } from './operators'
import {
  generateProcedurePreamble,
  procedureBlocks,
  procedureDefaults,
  registerProcedureCallbacks,
} from './procedures'
import { sensingBlocks, sensingDefaults } from './sensing'
import { registerSoundCallbacks, soundBlocks, soundDefaults } from './sound'
import {
  generateVariablePreamble,
  registerVariableCallbacks,
  variableBlocks,
  variableDefaults,
} from './variables'
import { BlockDefinition } from './common'
import {
  disableDisconnectedBlocks,
  disableNonOnPlayBlocks,
  disableOnPlayBlocks,
} from './utils'

// 全角数字→半角数字変換処理をインポート
import './numbers'

export * from './variables'

export const globalDefaults = {}

export const generatePreamble = (workspace: Blockly.Workspace) => {
  return [generateVariablePreamble, generateProcedurePreamble]
    .map((fn) => fn(workspace))
    .filter((code) => code && code.trim())
    .join('\n')
}

export const generateCode = (workspace: Blockly.Workspace) => {
  disableDisconnectedBlocks(workspace, true)
  disableOnPlayBlocks(workspace, true)
  let code = generatePreamble(workspace)
  code += luaGenerator.workspaceToCode(workspace)
  disableOnPlayBlocks(workspace, false)
  disableNonOnPlayBlocks(workspace, true)
  const onPlay = luaGenerator.workspaceToCode(workspace)
  if (onPlay) {
    code += `\n${onPlay}`
  }
  disableNonOnPlayBlocks(workspace, false)
  disableDisconnectedBlocks(workspace, false)
  return code
}

export const generateCodeHeadless = (
  workspaceData: Record<string, unknown>,
) => {
  const workspace = new Blockly.Workspace()
  Blockly.serialization.workspaces.load(workspaceData, workspace)
  const code = generateCode(workspace)
  workspace.dispose()
  return code
}

export const registerCallbacks = (workspace: Blockly.WorkspaceSvg) => {
  const unregisterMessageCallbacks = registerMessageCallbacks(workspace)
  registerProcedureCallbacks(workspace)
  registerSoundCallbacks(workspace)
  registerVariableCallbacks(workspace)
  // Dynamic category callbacks must be set up before mission callbacks.
  const unregisterMissionCallbacks = registerMissionCallbacks(workspace)

  // Return a function that unregisters all callbacks that need to be unregistered.
  return () => {
    unregisterMessageCallbacks?.()
    unregisterMissionCallbacks?.()
  }
}

const makeBlockCategory = (
  blocks: BlockDefinition[],
  categoryDefaults: Partial<BlockDefinition>,
) => {
  blocks.forEach((block) => {
    const { generator, icon, opcode, ...rest } = block
    Blockly.Blocks[opcode] = {
      init() {
        this.jsonInit({ ...globalDefaults, ...categoryDefaults, ...rest })
        if (icon) {
          this.addIcon(new icon(this))
        }
      },
    }
    luaGenerator.forBlock[opcode] = generator
  })
}

makeBlockCategory(controlBlocks, controlDefaults)
makeBlockCategory(emptyNumberBlocks, emptyNumberDefaults)
makeBlockCategory(eventBlocks, eventDefaults)
makeBlockCategory(looksBlocks, looksDefaults)
makeBlockCategory(movementBlocks, movementDefaults)
makeBlockCategory(operatorBlocks, operatorDefaults)
makeBlockCategory(procedureBlocks, procedureDefaults)
makeBlockCategory(sensingBlocks, sensingDefaults)
makeBlockCategory(soundBlocks, soundDefaults)
makeBlockCategory(variableBlocks, variableDefaults)
